import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Tour Context
 * Global state management for feature tours using react-joyride
 */
import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Joyride, { STATUS, EVENTS } from 'react-joyride';
import { TourTooltip } from '../components/TourTooltip';
import { getTutorialByTourId } from '../data/tours';
import { awardBadgeForTour } from '../utils/tutorialCompletion';
import { useAuthStore } from '../stores/authStore';
import { BaseModal } from '../components/shared/BaseModal';
import { Button } from '../components/PageStyles';
import { FaCheckCircle, FaPlayCircle, FaMedal } from 'react-icons/fa';
const TourContext = createContext(undefined);
export const useTour = () => {
    const context = useContext(TourContext);
    if (!context) {
        throw new Error('useTour must be used within TourProvider');
    }
    return context;
};
const COMPLETED_TOURS_KEY = 'royal-gems-completed-tours';
/**
 * Wait for an element to exist in the DOM
 * @param selector CSS selector
 * @param timeout Maximum time to wait in ms
 * @returns Promise that resolves when element is found or rejects on timeout
 */
const waitForElement = (selector, timeout = 5000) => {
    return new Promise((resolve, reject) => {
        const element = document.querySelector(selector);
        if (element) {
            return resolve(element);
        }
        const observer = new MutationObserver(() => {
            const element = document.querySelector(selector);
            if (element) {
                observer.disconnect();
                resolve(element);
            }
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
        setTimeout(() => {
            observer.disconnect();
            reject(new Error(`Element "${selector}" not found within ${timeout}ms`));
        }, timeout);
    });
};
export const TourProvider = ({ children }) => {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuthStore();
    const [currentTour, setCurrentTour] = useState(null);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [run, setRun] = useState(false);
    const onCompleteCallbackRef = useRef(null);
    const chainedCallbackRef = useRef(null); // Preserved across chained tours
    const joyrideHelpersRef = useRef(null);
    const [completedTours, setCompletedTours] = useState(() => {
        const stored = localStorage.getItem(COMPLETED_TOURS_KEY);
        return stored ? JSON.parse(stored) : [];
    });
    const [chapterCompleteData, setChapterCompleteData] = useState(null);
    // Sync completed tours to localStorage
    useEffect(() => {
        localStorage.setItem(COMPLETED_TOURS_KEY, JSON.stringify(completedTours));
    }, [completedTours]);
    const hasCompletedTour = useCallback((tourId) => {
        return completedTours.includes(tourId);
    }, [completedTours]);
    const markTourAsCompleted = useCallback((tourId) => {
        setCompletedTours(prev => {
            if (!prev.includes(tourId)) {
                return [...prev, tourId];
            }
            return prev;
        });
    }, []);
    const startTour = useCallback(async (tour, onComplete) => {
        console.log('🎯 Starting tour:', tour.tourId);
        console.log('📍 Tour route:', tour.route);
        console.log('📍 Current route:', window.location.pathname);
        // Store completion callback in ref (not state to avoid re-render issues)
        if (onComplete) {
            console.log('💾 Storing completion callback in ref - BADGE WILL BE AWARDED ON COMPLETION');
            onCompleteCallbackRef.current = onComplete;
            console.log('✅ Callback stored, ref.current =', onCompleteCallbackRef.current);
            // For memo-request-tour (first tour in chain), also store in chainedCallbackRef
            // This callback will be preserved and called only after the FINAL tour in the chain
            if (tour.tourId === 'memo-request-tour') {
                console.log('🔗 This is first tour in memo chain - storing callback for end of chain');
                chainedCallbackRef.current = onComplete;
            }
        }
        else {
            console.log('ℹ️ No completion callback provided - this is a DEMO tour (no badge awarded)');
            onCompleteCallbackRef.current = null;
        }
        // Navigate to the tour's route if different from current route
        if (tour.route && window.location.pathname !== tour.route) {
            console.log('🚀 Navigating to:', tour.route);
            navigate(tour.route);
            // Wait for navigation
            await new Promise(resolve => setTimeout(resolve, 1000));
        }
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
        await new Promise(resolve => setTimeout(resolve, 300));
        // Wait for the first tour element to exist
        const firstStepTarget = tour.steps[0]?.target;
        if (firstStepTarget && firstStepTarget !== 'body') {
            console.log('⏳ Waiting for first element:', firstStepTarget);
            try {
                await waitForElement(firstStepTarget, 10000);
                console.log('✅ First element found!');
            }
            catch (error) {
                console.error('❌ Tour element not found:', error);
                alert('Unable to start tour - the required elements are not visible on the page. Please make sure diamonds are loaded.');
                return;
            }
        }
        // Scroll the scrollable container to ensure first element is visible
        const diamondContainer = document.getElementById('diamond-results-container');
        const firstElement = document.querySelector(tour.steps[0]?.target);
        if (diamondContainer && firstElement) {
            console.log('📜 Scrolling diamond container to show first element');
            // Scroll the container to bring the element into view
            const containerRect = diamondContainer.getBoundingClientRect();
            const elementRect = firstElement.getBoundingClientRect();
            // Calculate scroll position within the container
            const scrollTop = elementRect.top - containerRect.top + diamondContainer.scrollTop - 100;
            diamondContainer.scrollTop = scrollTop;
        }
        // Wait for scroll to complete and layout to stabilize
        await new Promise(resolve => setTimeout(resolve, 300));
        // Verify all elements exist and check positioning
        console.log('🔍 Verifying all tour elements...');
        for (let i = 0; i < tour.steps.length; i++) {
            const step = tour.steps[i];
            if (step.target === 'body')
                continue;
            const element = document.querySelector(step.target);
            if (element) {
                const rect = element.getBoundingClientRect();
                console.log(`Step ${i + 1} "${step.target}": ✅ FOUND at`, {
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                    inViewport: rect.top >= 0 && rect.left >= 0 && rect.bottom <= window.innerHeight && rect.right <= window.innerWidth
                });
            }
            else {
                console.log(`Step ${i + 1} "${step.target}": ❌ NOT FOUND`);
            }
        }
        // Start the tour
        console.log('🎬 Starting tour now!');
        // Add body class to disable problematic CSS during tour
        document.body.classList.add('tour-active');
        setCurrentTour(tour);
        setRun(true);
    }, [navigate]);
    const stopTour = useCallback(() => {
        // Remove body class
        document.body.classList.remove('tour-active');
        setRun(false);
        setCurrentTour(null);
        onCompleteCallbackRef.current = null;
    }, []);
    const showChapterCompleteModal = useCallback((data) => {
        console.log('📋 Showing global chapter complete modal:', data);
        setChapterCompleteData(data);
    }, []);
    const handleJoyrideCallback = useCallback((data) => {
        const { status, type, index, action, step, lifecycle } = data;
        console.log('Tour callback:', { status, type, index, action, lifecycle });
        // Update current step index
        setCurrentStepIndex(index);
        // IMPORTANT: For chained tours, prevent the completion callback from being called
        // until the FINAL tour in the chain completes. This handles 'step:after' events
        // which fire before 'tour:end' events.
        const isChainedTour = currentTour?.tourId === 'memo-request-tour' ||
            currentTour?.tourId === 'memo-detail-modal-tour';
        // For chained tours, only proceed with chain logic on 'tour:end', skip all 'step:after' finish events
        if (status === STATUS.FINISHED && isChainedTour && type !== 'tour:end') {
            console.log('⏸️ Chained tour finished event (step:after) - waiting for tour:end to chain');
            return; // Don't trigger completion callback, wait for tour:end to handle chaining
        }
        // Chain memo request tours - when MEMO_REQUEST_TOUR finishes, open modal and start MEMO_DETAIL_MODAL_TOUR
        // Only trigger on 'tour:end' type to avoid double-triggering
        if (status === STATUS.FINISHED &&
            type === 'tour:end' &&
            currentTour?.tourId === 'memo-request-tour') {
            console.log('🎯 MEMO_REQUEST_TOUR finished - opening DiamondDetailModal and starting next tour');
            // Stop current tour first
            stopTour();
            // Dispatch event to trigger DiamondDetailModal opening
            window.dispatchEvent(new CustomEvent('openDiamondDetailModalForTour'));
            // Wait for the modal to open, then start the next tour
            waitForElement('#tour-diamond-detail-modal', 5000).then(() => {
                console.log('✅ DiamondDetailModal opened - starting MEMO_DETAIL_MODAL_TOUR');
                setTimeout(() => {
                    // Import and start the next tour
                    import('../data/tours').then(({ TOURS }) => {
                        startTour(TOURS.MEMO_DETAIL_MODAL);
                    });
                }, 500);
            }).catch((err) => {
                console.error('❌ DiamondDetailModal failed to open:', err);
            });
            return; // Don't process normal finish logic
        }
        // Chain memo detail modal tour - when it finishes, open RequestMemoModal and start MEMO_FORM_TOUR
        // Only trigger on 'tour:end' type to avoid double-triggering
        if (status === STATUS.FINISHED &&
            type === 'tour:end' &&
            currentTour?.tourId === 'memo-detail-modal-tour') {
            console.log('🎯 MEMO_DETAIL_MODAL_TOUR finished - opening RequestMemoModal and starting next tour');
            // Stop current tour first
            stopTour();
            // Dispatch event to trigger RequestMemoModal opening
            window.dispatchEvent(new CustomEvent('openMemoModalForTour'));
            // Wait for the modal to open, then start the next tour
            waitForElement('#tour-memo-modal', 3000).then(() => {
                console.log('✅ RequestMemoModal opened - starting MEMO_FORM_TOUR');
                setTimeout(() => {
                    // Import and start the next tour
                    import('../data/tours').then(({ TOURS }) => {
                        startTour(TOURS.MEMO_FORM);
                    });
                }, 500);
            }).catch((err) => {
                console.error('❌ RequestMemoModal failed to open:', err);
            });
            return; // Don't process normal finish logic
        }
        // Special handling for RG Appraisal tour - switch to history tab after step 3 completes
        if (type === EVENTS.STEP_AFTER &&
            currentTour?.tourId === 'rg-appraisal-showcase-tour' &&
            index === 3) {
            console.log('🎯 Completed history tab step - switching to history tab for next steps');
            const filterBar = document.querySelector('#tour-filter-bar');
            if (!filterBar) {
                console.log('📋 History tab not active - triggering tab switch via window event');
                // Pause tour briefly
                setRun(false);
                // Dispatch custom event to trigger tab switch
                window.dispatchEvent(new CustomEvent('switchToHistoryTabForTour'));
                // Wait for filter bar to appear, then resume tour
                waitForElement('#tour-filter-bar', 3000).then(() => {
                    console.log('✅ History tab activated - resuming tour');
                    setTimeout(() => {
                        setRun(true);
                    }, 300);
                }).catch((err) => {
                    console.error('❌ History tab failed to activate in time:', err);
                    setRun(true); // Resume anyway
                });
            }
        }
        // Handle element not found
        if (type === EVENTS.TARGET_NOT_FOUND) {
            console.error('Tour element not found for step:', currentTour?.steps[index]);
            // Special case: if memo modal elements not found on steps 2-5, give it more time
            // Step 2 is Request Memo button in DiamondDetailModal, steps 3-5 are in RequestMemoModal
            if (currentTour?.tourId === 'memo-request-tour' && index >= 2 && index <= 5) {
                console.log('🔄 Modal element not found on step', index, '- waiting for modal to open...');
                // Modal should be opening, give it time - don't stop the tour
                // React-joyride will keep checking
                return;
            }
            // Special case: if RG Appraisal history elements not found on steps 4-5, give it more time
            if (currentTour?.tourId === 'rg-appraisal-showcase-tour' && (index === 4 || index === 5)) {
                console.log('🔄 History tab element not found - waiting for tab to switch...');
                // Tab should be switching, give it time - don't stop the tour
                return;
            }
            // End tour if element not found
            stopTour();
            return;
        }
        // Handle STEP_BEFORE - scroll to first step if needed
        if (type === EVENTS.STEP_BEFORE && index === 0) {
            const currentStep = currentTour?.steps[index];
            if (currentStep && currentStep.target !== 'body') {
                setTimeout(() => {
                    const el = document.querySelector(currentStep.target);
                    if (el) {
                        const rect = el.getBoundingClientRect();
                        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                        const elementTop = rect.top + scrollTop;
                        // Scroll main window to element with offset for header
                        const headerOffset = 120;
                        const targetPosition = elementTop - headerOffset;
                        console.log('📜 Scrolling to first step element:', currentStep.target);
                        window.scrollTo({
                            top: targetPosition,
                            behavior: 'smooth'
                        });
                    }
                }, 100);
            }
        }
        // Tour finished or skipped
        if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
            if (status === STATUS.SKIPPED) {
                console.log('❌ Tour skipped/closed - NOT awarding badge');
            }
            if (currentTour && status === STATUS.FINISHED) {
                console.log('✅ Tour completed successfully - awarding badge');
                markTourAsCompleted(currentTour.tourId);
                // Automatically award badge if this tour has a matching tutorial
                const tutorialMeta = getTutorialByTourId(currentTour.tourId);
                if (tutorialMeta && isAuthenticated && user) {
                    console.log('🎯 Found matching tutorial - automatically awarding badge');
                    console.log('📚 Tutorial:', tutorialMeta.title);
                    console.log('🏅 Badge:', tutorialMeta.badge.name);
                    // Get current tutorial progress from user profile
                    // Note: User object from authStore may not have latest tutorials, so we need to fetch it
                    // For now, we'll pass empty arrays and let the backend merge properly
                    const currentCompleted = [];
                    const currentBadges = [];
                    // Award badge
                    awardBadgeForTour(currentTour.tourId, user.id, currentCompleted, currentBadges, 1 // Will be updated when we have more tutorials
                    ).then(result => {
                        if (result.success) {
                            console.log('✅ Badge awarded successfully from tour completion!');
                        }
                        else {
                            console.error('❌ Failed to award badge:', result.error);
                        }
                    });
                }
                else {
                    console.log('ℹ️ No tutorial mapping for this tour - demo mode');
                }
                // Also call custom completion callback if provided (for additional logic)
                // For chained tours, use chainedCallbackRef (preserved from first tour in chain)
                const callbackToUse = onCompleteCallbackRef.current || chainedCallbackRef.current;
                if (callbackToUse) {
                    console.log('✅ Calling custom completion callback');
                    if (chainedCallbackRef.current && !onCompleteCallbackRef.current) {
                        console.log('🔗 Using chained callback from memo tour chain');
                    }
                    try {
                        callbackToUse();
                        console.log('✅ Custom callback executed successfully');
                    }
                    catch (error) {
                        console.error('❌ Error executing custom callback:', error);
                    }
                    // Clear the chained callback after it's been used
                    chainedCallbackRef.current = null;
                }
            }
            stopTour();
            return;
        }
        // Log step transitions (Joyride manages index internally)
        if (type === EVENTS.STEP_AFTER) {
            console.log('Step transition:', { action, fromIndex: index });
        }
    }, [currentTour, markTourAsCompleted, stopTour, user, isAuthenticated]);
    // Convert FeatureTourStep[] to Joyride Step[]
    const steps = currentTour?.steps.map((step, index) => ({
        ...step,
        target: step.target,
        content: step.content,
        title: step.title,
        placement: step.placement || 'auto',
        disableBeacon: step.disableBeacon ?? true,
        spotlightClicks: step.spotlightClicks ?? false,
        disableScrolling: true, // Always disable auto-scroll to prevent nested container issues
        hideFooter: step.hideFooter ?? false,
        offset: 15
    })) || [];
    return (_jsxs(TourContext.Provider, { value: {
            startTour,
            stopTour,
            isTourActive: run && currentTour !== null,
            currentTour,
            currentStepIndex,
            hasCompletedTour,
            markTourAsCompleted,
            showChapterCompleteModal
        }, children: [children, chapterCompleteData && (_jsx(BaseModal, { isOpen: true, onClose: () => setChapterCompleteData(null), title: chapterCompleteData.nextChapter ? "Chapter Complete!" : "Tutorial Complete!", maxWidth: "600px", closeOnOverlayClick: false, footer: _jsx(_Fragment, { children: chapterCompleteData.nextChapter ? (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "secondary", onClick: () => setChapterCompleteData(null), children: "Continue Later" }), _jsxs(Button, { variant: "primary", onClick: () => {
                                    chapterCompleteData.onContinue();
                                    setChapterCompleteData(null);
                                }, children: [_jsx(FaPlayCircle, {}), " Continue to Next Chapter"] })] })) : (_jsxs(Button, { variant: "primary", onClick: () => {
                            chapterCompleteData.onContinue();
                            setChapterCompleteData(null);
                        }, style: { width: '100%' }, children: [_jsx(FaCheckCircle, {}), " Go Back to Learning Center"] })) }), children: _jsxs("div", { style: { textAlign: 'center', padding: '20px 0' }, children: [_jsx("div", { style: { fontSize: '48px', marginBottom: '16px' }, children: _jsx(FaCheckCircle, { style: { color: '#22c55e' } }) }), _jsx("h3", { style: { color: 'var(--text-primary)', fontSize: '20px', marginBottom: '12px' }, children: chapterCompleteData.completedChapter }), _jsxs("div", { style: {
                                background: 'var(--bg-tertiary)',
                                padding: '16px',
                                borderRadius: '8px',
                                margin: '24px 0',
                                border: '1px solid var(--border-color)'
                            }, children: [_jsxs("div", { style: { fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '8px' }, children: [chapterCompleteData.tutorialTitle, " - Progress"] }), _jsxs("div", { style: { fontSize: '32px', fontWeight: 'bold', color: 'var(--text-primary)' }, children: [chapterCompleteData.completedCount, " / ", chapterCompleteData.totalCount] }), _jsx("div", { style: { fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }, children: "chapters complete" })] }), chapterCompleteData.nextChapter && (_jsxs("div", { style: { color: 'var(--text-secondary)', fontSize: '15px', lineHeight: '1.6' }, children: [_jsx("strong", { style: { color: 'var(--text-primary)' }, children: "Next Chapter:" }), _jsx("br", {}), chapterCompleteData.nextChapter.title] })), !chapterCompleteData.nextChapter && chapterCompleteData.badgeEarned && (_jsxs("div", { style: {
                                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                padding: '20px',
                                borderRadius: '8px',
                                color: 'white',
                                marginTop: '8px'
                            }, children: [_jsx("div", { style: { fontSize: '48px', marginBottom: '12px' }, children: _jsx(FaMedal, {}) }), _jsx("div", { style: { fontSize: '18px', fontWeight: '700', marginBottom: '8px' }, children: "Badge Earned!" }), _jsx("div", { style: { fontSize: '20px', fontWeight: '600' }, children: chapterCompleteData.badgeEarned.name }), _jsx("div", { style: { fontSize: '14px', opacity: 0.9, marginTop: '4px', textTransform: 'uppercase', letterSpacing: '1px' }, children: chapterCompleteData.badgeEarned.tier })] }))] }) })), currentTour && (_jsx(Joyride, { steps: steps, run: run, continuous: currentTour.continuous ?? true, scrollToFirstStep: false, showProgress: currentTour.showProgress ?? true, showSkipButton: false, disableOverlay: currentTour.disableOverlay ?? false, disableOverlayClose: true, disableCloseOnEsc: false, hideCloseButton: false, disableScrolling: true, disableScrollParentFix: true, floaterProps: {
                    disableAnimation: true,
                    hideArrow: false,
                    offset: 0,
                    styles: {
                        floater: {
                            filter: 'none'
                        }
                    }
                }, getHelpers: (helpers) => {
                    joyrideHelpersRef.current = helpers;
                }, callback: handleJoyrideCallback, tooltipComponent: TourTooltip, styles: {
                    options: {
                        arrowColor: '#1a1a1a',
                        backgroundColor: '#1a1a1a',
                        primaryColor: '#3b82f6',
                        textColor: '#ffffff',
                        overlayColor: 'rgba(0, 0, 0, 0.75)',
                        spotlightShadow: '0 0 15px rgba(59, 130, 246, 0.5)',
                        zIndex: 99999
                    },
                    overlay: {
                        zIndex: 99998,
                        position: 'fixed', // Fix spotlight positioning when page is scrolled
                        top: 0,
                        left: 0
                    },
                    tooltip: {
                        padding: 0
                    },
                    spotlight: {
                        borderRadius: '8px'
                    },
                    buttonNext: {
                        backgroundColor: '#3b82f6',
                        borderRadius: '4px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: 600
                    },
                    buttonBack: {
                        color: '#9ca3af',
                        marginRight: '8px'
                    },
                    buttonSkip: {
                        color: '#9ca3af',
                        fontSize: '13px'
                    }
                } }))] }));
};
